import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../components/ui/card';
import { Badge } from '../../../../components/ui/badge';
import { Button } from '../../../../components/ui/button';
import { useNavigate } from 'react-router-dom';
import { Calendar, ArrowRight } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { cn } from '../../../../lib/utils';

/**
 * Deadline Tracker Widget
 * Lists open requests ordered by urgency (overdue first, then nearest deadline)
 * Shows: reference, client, type, received date, statutory deadline, status badge
 */
export function DeadlineTrackerWidget({ dsrs = [], entities = [] }) {
  const navigate = useNavigate();

  // Build entity map for jurisdiction lookup
  const entityMap = {};
  entities.forEach(entity => {
    entityMap[entity.id] = entity;
  });

  // Helper to determine deadline status from days remaining
  const getStatusFromDaysRemaining = (daysRemaining, deadline) => {
    if (!deadline) return 'unset';
    if (daysRemaining < 0) return 'overdue';
    if (daysRemaining <= 7) return 'due_soon';
    return 'on_track';
  };

  // Filter open requests and use API-provided deadline data
  const openRequests = dsrs
    .filter(dsr => {
      const isOpen = dsr.status && !['responded', 'closed', 'withdrawn', 'refused'].includes(dsr.status);
      return isOpen;
    })
    .map(dsr => {
      // Use API-provided deadline and days_remaining if available
      const deadline = dsr.deadline ? parseISO(dsr.deadline) : null;
      const daysRemaining = dsr.days_remaining !== undefined ? dsr.days_remaining : null;
      const status = getStatusFromDaysRemaining(daysRemaining, deadline);

      return {
        ...dsr,
        deadlineInfo: {
          deadline,
          daysRemaining,
          status
        },
        entityName: dsr.entity_name || 'Unknown Entity'
      };
    })
    // Sort by urgency: overdue first, then due soon, then on track
    .sort((a, b) => {
      const statusOrder = { overdue: 0, due_soon: 1, on_track: 2, unset: 3 };
      const statusCompare = statusOrder[a.deadlineInfo.status] - statusOrder[b.deadlineInfo.status];
      if (statusCompare !== 0) return statusCompare;

      // Within same status, sort by days remaining (ascending)
      const daysA = a.deadlineInfo.daysRemaining ?? Infinity;
      const daysB = b.deadlineInfo.daysRemaining ?? Infinity;
      return daysA - daysB;
    })
    .slice(0, 5); // Show top 5 most urgent

  if (openRequests.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-primary" />
            Deadline Tracker
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground text-center py-6">
            No open requests — all caught up!
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calendar className="h-5 w-5 text-primary" />
          Deadline Tracker
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {openRequests.map(dsr => {
            const statusColors = {
              overdue: 'bg-red-100 dark:bg-red-950 text-red-700 dark:text-red-300',
              due_soon: 'bg-amber-100 dark:bg-amber-950 text-amber-700 dark:text-amber-300',
              on_track: 'bg-green-100 dark:bg-green-950 text-green-700 dark:text-green-300',
              unset: 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'
            };

            const statusLabels = {
              overdue: 'Overdue',
              due_soon: 'Due Soon',
              on_track: 'On Track',
              unset: 'Unset'
            };

            return (
              <div
                key={dsr.id}
                className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors group cursor-pointer"
                onClick={() => navigate(`/compliance/dsr/workflow/${dsr.id}`)}
              >
                {/* Left side - Reference and details */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2">
                    <span className="font-mono text-sm font-semibold whitespace-nowrap">{dsr.dsr_reference || dsr.reference}</span>
                    <span className="text-sm text-slate-700 dark:text-slate-300 truncate">
                      {dsr.request_type || 'Request'} — {dsr.entityName}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Received {dsr.date_received ? format(parseISO(dsr.date_received), 'dd MMM yyyy') : 'Unknown date'}
                  </p>
                </div>

                {/* Right side - Deadline, status, arrow */}
                <div className="flex items-center gap-4 ml-4 flex-shrink-0">
                  <div className="text-right min-w-[120px]">
                    <p className="text-sm font-medium">
                      {dsr.deadlineInfo.deadline
                        ? format(dsr.deadlineInfo.deadline, 'dd MMM yyyy')
                        : 'Unset'}
                    </p>
                    {dsr.deadlineInfo.daysRemaining !== null && (
                      <p className="text-xs text-muted-foreground">
                        {dsr.deadlineInfo.daysRemaining < 0
                          ? `${Math.abs(dsr.deadlineInfo.daysRemaining)} days overdue`
                          : `${dsr.deadlineInfo.daysRemaining} days left`}
                      </p>
                    )}
                  </div>

                  <Badge className={cn(statusColors[dsr.deadlineInfo.status], 'whitespace-nowrap')}>
                    {statusLabels[dsr.deadlineInfo.status]}
                  </Badge>

                  <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:translate-x-0.5 transition-transform flex-shrink-0" />
                </div>
              </div>
            );
          })}
        </div>

        {dsrs.filter(d => d.status && !['responded', 'closed', 'withdrawn', 'refused'].includes(d.status)).length > 5 && (
          <div className="pt-3 border-t mt-3">
            <Button
              variant="ghost"
              size="sm"
              className="w-full"
              onClick={() => navigate('/compliance/dsr')}
            >
              View all open requests →
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default DeadlineTrackerWidget;
